'use client'

/**
 * WatchlistPageClient — the full-page /watchlist home. A rich, sortable view of everything a trader is
 * tracking (live price, day %, mini-chart, remove, game buy/sell), reusing EnhancedWatchlist. Add a
 * ticker inline; stays in sync with WatchStars everywhere via the `watchlist-changed` event. The quick
 * left-pane is the glance; this is the workbench.
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSession, signIn } from 'next-auth/react'
import Link from 'next/link'
import { Plus, Search, Star, Filter, TrendingUp, TrendingDown, Flame, Newspaper } from 'lucide-react'
import Watchlist, { type WatchlistInsightLite } from './EnhancedWatchlist'

type WLItems = React.ComponentProps<typeof Watchlist>['items']

export default function WatchlistPageClient() {
  const { status } = useSession()
  const [items, setItems] = useState<WLItems>([])
  const [loading, setLoading] = useState(true)
  const [newSym, setNewSym] = useState('')
  const [insights, setInsights] = useState<Record<string, WatchlistInsightLite>>({})

  const load = useCallback(async () => {
    try {
      const r = await fetch(`/api/user/watchlist/realtime?t=${Date.now()}`, { cache: 'no-store' })
      const j = r.ok ? await r.json() : null
      setItems(Array.isArray(j?.watchlist) ? j.watchlist : [])
    } catch { /* keep */ } finally { setLoading(false) }
  }, [])

  /**
   * Insights (today's alert counts + news catalyst) load SEPARATELY from the quote poll on purpose.
   * `load()` is the fast path the board re-runs to refresh prices; this one runs a grouped alert query
   * and a bounded news fan-out. Folding them together would make every price refresh pay for the news
   * lookup, so prices paint first and the fire badges and "why" cards fill in behind them.
   */
  const loadInsights = useCallback(async () => {
    try {
      const r = await fetch('/api/user/watchlist/insights', { cache: 'no-store' })
      const j = r.ok ? await r.json() : null
      if (j?.insights) setInsights(j.insights)
    } catch { /* decoration only: the board is fully usable without it */ }
  }, [])

  useEffect(() => {
    if (status === 'authenticated') { load(); loadInsights() }
    else if (status !== 'loading') setLoading(false)
  }, [status, load, loadInsights])

  // Stay in sync with WatchStar toggles elsewhere on the site.
  useEffect(() => {
    const on = () => { if (status === 'authenticated') load() }
    window.addEventListener('watchlist-changed', on)
    return () => window.removeEventListener('watchlist-changed', on)
  }, [status, load])

  const saveList = async (syms: string[]) => {
    await fetch('/api/user/watchlist', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ watchlist: syms }) }).catch(() => {})
    window.dispatchEvent(new Event('watchlist-changed'))
    load()
  }
  // "My market at a glance" — advancers/decliners, average move, best/worst among the tracked names.
  const summary = useMemo(() => {
    const vals = items
      .map((i) => ({ symbol: i.symbol, pct: typeof i.dailyPercentChange === 'number' ? i.dailyPercentChange : null }))
      .filter((x): x is { symbol: string; pct: number } => x.pct != null && Number.isFinite(x.pct))
    if (!vals.length) return null
    const up = vals.filter((x) => x.pct > 0).length
    const down = vals.filter((x) => x.pct < 0).length
    const avg = vals.reduce((s, x) => s + x.pct, 0) / vals.length
    const sorted = [...vals].sort((a, b) => b.pct - a.pct)
    // Alert-side metrics. Computed here rather than in the child so the header can lead with "how
    // busy is my list today", which is the question this page exists to answer at a glance.
    const entries = Object.entries(insights)
    const totalAlerts = entries.reduce((s, [, v]) => s + (v?.alertsToday || 0), 0)
    const withNews = entries.filter(([, v]) => !!v?.catalyst).length
    const hottest = entries
      .filter(([, v]) => (v?.alertsToday || 0) > 0)
      .sort((a, b) => (b[1].alertsToday || 0) - (a[1].alertsToday || 0))[0]
    // ⚠ WHICH SESSION THE COUNT DESCRIBES, and it must be SHOWN. getWatchlistInsights now falls back
    // to the last session that actually had alerts, because pinning to the current trading day meant
    // every badge read 0 outside market hours and the feature looked broken (owner 2026-08-09,
    // "alert count does not show"). Measured on prod 2026-08-16, a Sunday: current day 0 alerts,
    // previous session 18,410. The fallback is only honest BECAUSE it is labelled — "12 alerts" means
    // something very different today than on Friday, so an unlabelled fallback would be worse than
    // the zero it replaced. Every entry carries the same window, so reading the first is sufficient.
    const win = entries.find(([, v]) => v?.alertWindow)?.[1]?.alertWindow ?? 'none'
    const alertLabel = win === 'today' ? 'today' : win === 'last-session' ? 'last session' : null
    return {
      count: items.length, up, down, avg, top: sorted[0], bottom: sorted[sorted.length - 1],
      totalAlerts, withNews, alertLabel,
      hottest: hottest ? { symbol: hottest[0], n: hottest[1].alertsToday } : null,
    }
  }, [items, insights])

  const remove = (sym: string) => saveList(items.map((i) => i.symbol).filter((s) => s !== sym))
  const add = async () => {
    const s = newSym.toUpperCase().replace(/[^A-Z0-9.\-]/g, '').slice(0, 12)
    setNewSym('')
    if (!s || items.some((i) => i.symbol === s)) return
    await saveList([s, ...items.map((i) => i.symbol)])
  }

  if (status !== 'authenticated' && status !== 'loading') {
    return (
      <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-10 text-center">
        <Star className="mx-auto mb-3 h-8 w-8 text-amber-400" />
        <p className="text-lg font-bold text-white">Build your watchlist</p>
        <p className="mx-auto mt-1 max-w-md text-sm text-zinc-400">Sign in to track your stocks with live prices, mini-charts, unusual-volume alerts and one-click price alerts - all in one place.</p>
        <button onClick={() => signIn()} className="mt-4 rounded-lg bg-amber-500 px-5 py-2.5 text-sm font-bold text-black transition-colors hover:bg-amber-400">Sign in</button>
      </div>
    )
  }

  return (
    <div>
      {/* Add ticker */}
      <div className="mb-5 flex items-center gap-2">
        <div className="flex flex-1 items-center gap-2 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3 py-2 focus-within:border-amber-500/50 sm:max-w-xs">
          <Search className="h-4 w-4 shrink-0 text-zinc-500" />
          <input value={newSym} onChange={(e) => setNewSym(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') add() }}
            placeholder="Add a ticker…" className="w-full bg-transparent text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none" aria-label="Add a ticker" />
        </div>
        <button type="button" onClick={add} disabled={!newSym.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-3.5 py-2 text-sm font-bold text-black transition-colors hover:bg-amber-400 disabled:opacity-50">
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      {/* ── MY MARKET AT A GLANCE ─────────────────────────────────────────────────────────────────
          Owner 2026-08-09: "i like the little custom metrics at the top, let's expand this to be
          better and more visually polished." Two rows now: BREADTH (what the list is doing) and
          ACTIVITY (how loud it is today), because those are different questions and mixing them into
          one four-up made the alert count read like just another price stat.
          ⚠ Alert cards render only once insights have loaded. A "0 alerts" card that is really
          "not fetched yet" is a lie the reader cannot detect. */}
      {summary && (
        <div className="mb-5 space-y-2.5">
          <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-4">
            <div className="rounded-xl border border-zinc-800/70 bg-gradient-to-br from-zinc-900/70 to-zinc-950 px-3.5 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Tracked · Today</div>
              <div className="mt-1 flex items-baseline gap-2 text-sm font-bold">
                <span className="text-lg text-zinc-100 tabular-nums">{summary.count}</span>
                <span className="inline-flex items-center gap-1 text-emerald-400"><TrendingUp className="h-3 w-3" />{summary.up}</span>
                <span className="inline-flex items-center gap-1 text-rose-400"><TrendingDown className="h-3 w-3" />{summary.down}</span>
              </div>
              {/* Breadth bar: green/red share of the list, readable without parsing two numbers. */}
              {(summary.up + summary.down) > 0 && (
                <div className="mt-2 flex h-1 overflow-hidden rounded-full bg-zinc-800">
                  <div className="bg-emerald-500/70" style={{ width: `${(summary.up / (summary.up + summary.down)) * 100}%` }} />
                  <div className="bg-rose-500/70" style={{ width: `${(summary.down / (summary.up + summary.down)) * 100}%` }} />
                </div>
              )}
            </div>
            <div className="rounded-xl border border-zinc-800/70 bg-gradient-to-br from-zinc-900/70 to-zinc-950 px-3.5 py-3">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Avg move</div>
              <div className={`mt-1 text-lg font-bold tabular-nums ${summary.avg >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>{summary.avg >= 0 ? '+' : ''}{summary.avg.toFixed(2)}%</div>
            </div>
            <Link href={`/stocks/${encodeURIComponent(summary.top.symbol)}`} className="rounded-xl border border-zinc-800/70 bg-gradient-to-br from-emerald-500/[0.06] to-zinc-950 px-3.5 py-3 transition-colors hover:border-emerald-500/40">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Top today</div>
              <div className="mt-1 flex items-baseline gap-1.5 font-bold"><span className="text-white">${summary.top.symbol}</span><span className="tabular-nums text-emerald-400">{summary.top.pct >= 0 ? '+' : ''}{summary.top.pct.toFixed(1)}%</span></div>
            </Link>
            <Link href={`/stocks/${encodeURIComponent(summary.bottom.symbol)}`} className="rounded-xl border border-zinc-800/70 bg-gradient-to-br from-rose-500/[0.06] to-zinc-950 px-3.5 py-3 transition-colors hover:border-rose-500/40">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Worst today</div>
              <div className="mt-1 flex items-baseline gap-1.5 font-bold"><span className="text-white">${summary.bottom.symbol}</span><span className="tabular-nums text-rose-400">{summary.bottom.pct >= 0 ? '+' : ''}{summary.bottom.pct.toFixed(1)}%</span></div>
            </Link>
          </div>

          {Object.keys(insights).length > 0 && (
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              <div className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/[0.07] to-zinc-950 px-3.5 py-3">
                {/* ⚠ THE LABEL IS NOT DECORATION. The count falls back to the last session when the
                    current one is empty, so a hardcoded "Alerts today" would have been wrong every
                    weekend and every pre-market — stating a Friday number as today's. */}
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-amber-400/80"><Flame className="h-3 w-3" /> Alerts {summary.alertLabel ?? 'today'}</div>
                <div className="mt-1 text-lg font-bold tabular-nums text-amber-300">{summary.totalAlerts}</div>
              </div>
              {summary.hottest ? (
                <Link href={`/stocks/${encodeURIComponent(summary.hottest.symbol)}`} className="rounded-xl border border-zinc-800/70 bg-gradient-to-br from-zinc-900/70 to-zinc-950 px-3.5 py-3 transition-colors hover:border-amber-500/40">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Most active</div>
                  <div className="mt-1 flex items-baseline gap-1.5 font-bold"><span className="text-white">${summary.hottest.symbol}</span><span className="tabular-nums text-amber-300">{summary.hottest.n}</span></div>
                </Link>
              ) : (
                <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/40 px-3.5 py-3">
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">Most active</div>
                  <div className="mt-1 text-sm text-zinc-500">Quiet so far</div>
                </div>
              )}
              <div className="rounded-xl border border-zinc-800/70 bg-gradient-to-br from-zinc-900/70 to-zinc-950 px-3.5 py-3">
                <div className="flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-500"><Newspaper className="h-3 w-3" /> With a catalyst</div>
                <div className="mt-1 text-lg font-bold tabular-nums text-zinc-100">{summary.withNews}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-sm text-zinc-500">Loading your watchlist…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-zinc-800/70 bg-zinc-900/40 p-10 text-center">
          <Star className="mx-auto mb-3 h-8 w-8 text-zinc-600" />
          <p className="text-base font-bold text-white">Your watchlist is empty</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-zinc-400">Add a ticker above, or discover names to watch.</p>
          <div className="mt-4 flex items-center justify-center gap-2">
            <Link prefetch={false} href="/screener" className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3.5 py-2 text-sm font-semibold text-zinc-200 hover:border-amber-500/40 hover:text-amber-300"><Filter className="h-4 w-4" /> Open the screener</Link>
            <Link href="/pulse" className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-700 px-3.5 py-2 text-sm font-semibold text-zinc-200 hover:border-amber-500/40 hover:text-amber-300">See what's trending</Link>
          </div>
        </div>
      ) : (
        <Watchlist items={items} onRemove={remove} onRefresh={load} isOwnProfile insights={insights} />
      )}
    </div>
  )
}
